import {
  abbreviateNumber,
  applyRoundingStrategy,
  RoundingStrategy
} from '../../utils/number'
import {ShortCurrencySymbols} from './price'

interface CommonNumberFormattingProps {
  locale: string
  value: number
  currency?: string
  roundingStrategy?: RoundingStrategy
  currencyDisplayVariant?: CurrencyDisplayVariant
}
export type CurrencyDisplayVariant = Intl.NumberFormatOptions['currencyDisplay']

interface AbbreviatedFormattedNumberProps extends CommonNumberFormattingProps {
  useShortCurrency: boolean
}

interface PriceNumberProps extends CommonNumberFormattingProps {
  minimumFractionDigits?: number
  maximumFractionDigits?: number
}

const isLetter = (str: string) =>
  Boolean(str.length === 1 && str.match(/[a-z]/i))

const localizePrice = (
  parts: Intl.NumberFormatPart[],
  abbreviatedPriceNumber: number,
  useShortCurrency: boolean
) => {
  const fraction = (abbreviatedPriceNumber + '').split('.')[1] || '' // the fraction from parts is 2dp, but we don't want that for the abbreviation, so we use the abbreviatedPriceNumber fraction instead
  const price = parts.reduce((prev, curr) => {
    if (curr.type === 'currency' || curr.type === 'literal') {
      return useShortCurrency ? `${prev}` : `${prev}${curr.value}`
    }
    if (curr.type === 'decimal') {
      return fraction.length > 0 ? `${prev}${curr.value}` : `${prev}`
    }
    if (curr.type === 'fraction') {
      return `${prev}${fraction}`
    }
    return `${prev}${curr.value}`
  }, '')

  return price
}

/**
 * Formats a numeric price value into an abbreviated, localized currency string.
 *
 * Abbreviations use suffixes such as `k` (thousands) or `M` (millions),
 * depending on the value. Optionally replaces the currency symbol with a short variant
 * if `useShortCurrency` is enabled. Applies a rounding strategy before abbreviation.
 * @param params - The configuration object.
 * @param params.locale - The BCP 47 language tag for locale formatting (e.g., `"en-US"`, `"nl-NL"`).
 * @param params.roundingStrategy - The rounding strategy to apply before abbreviation (e.g., `"round"`, `"floor"`, `"ceil"`).
 * @param params.value - The numeric value to format.
 * @param params.currency - The ISO 4217 currency code (e.g., `"USD"`, `"EUR"`).
 * @param params.useShortCurrency - Whether to use a short currency symbol (e.g., `$` → `US$`, `€` → `EUR`).
 * @param params.currencyDisplayVariant - Optional currency display style (e.g., `"symbol"`, `"code"`, `"name"`).
 * @returns The abbreviated and localized currency string (e.g., `"$1.2k"`, `"€3M"`), or an empty string if abbreviation fails.
 */
export const formatAbbreviatedPriceValue = ({
  locale,
  roundingStrategy,
  value,
  currency,
  useShortCurrency,
  currencyDisplayVariant
}: AbbreviatedFormattedNumberProps) => {
  const shortCurrencySymbol = useShortCurrency
    ? ShortCurrencySymbols[currency as keyof typeof ShortCurrencySymbols] || ''
    : ''
  const abbreviatedPrice = abbreviateNumber(
    applyRoundingStrategy(roundingStrategy, value)
  )
  if (!abbreviatedPrice) return ''

  const lastChar = abbreviatedPrice.toString().slice(-1)
  const isLastCharLetter = isLetter(lastChar)
  const abbreviatedPriceEndLetter = isLastCharLetter ? lastChar : '' // k or M
  const abbreviatedPriceNumber = isLastCharLetter
    ? Number(abbreviatedPrice.toString().slice(0, -1))
    : Number(abbreviatedPrice)

  const parts = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    currencyDisplay: currencyDisplayVariant || undefined
  }).formatToParts(abbreviatedPriceNumber)

  const localizedPrice = localizePrice(
    parts,
    abbreviatedPriceNumber,
    useShortCurrency
  )

  return `${shortCurrencySymbol}${localizedPrice}${abbreviatedPriceEndLetter}`
}

/**
 * Formats a numeric price value into a localized currency string.
 *
 * Uses the `Intl.NumberFormat` API to format the number according to the specified locale,
 * currency, and fraction digit rules. It also applies a rounding strategy before formatting.
 * @param params - The configuration object.
 * @param params.locale - The BCP 47 language tag for locale formatting (e.g., `"en-US"`, `"nl-NL"`).
 * @param params.roundingStrategy - The rounding strategy to apply before formatting (e.g., `"round"`, `"floor"`, `"ceil"`).
 * @param params.value - The numeric value to format.
 * @param params.currency - The ISO 4217 currency code (e.g., `"USD"`, `"EUR"`).
 * @param params.minimumFractionDigits - The minimum number of decimal places to display.
 * @param params.maximumFractionDigits - The maximum number of decimal places to display.
 * @param params.currencyDisplayVariant - Optional currency display style (e.g., `"symbol"`, `"code"`, `"name"`).
 * @returns The formatted currency string.
 */
export const formatPriceValue = ({
  locale,
  roundingStrategy,
  value,
  currency,
  minimumFractionDigits,
  maximumFractionDigits,
  currencyDisplayVariant
}: PriceNumberProps) =>
  new Intl.NumberFormat(locale, {
    minimumFractionDigits,
    maximumFractionDigits,
    style: 'currency',
    currency,
    currencyDisplay: currencyDisplayVariant || undefined
  }).format(applyRoundingStrategy(roundingStrategy, value))
